import i18n from "i18next";
import {LazyMultiSelectField} from "src/components/common/inputFields/LazyMultiSelectField.tsx";
import {AavoTextField} from "src/components/common/inputFields/AavoTextField.tsx";
import {booleanColumn, dateTimeColumn, integerColumn, textColumn} from "src/components/common/dataGrid/columns.tsx";
import {DocumentForm} from "src/components/views/documents/DocumentForm.tsx";
import {DocumentsOfObjectDataGridApi} from "src/api/generated/documents/api/documentsOfObjectDataGridApi.ts";
import {
    DocumentsOfObjectsDataGridContextMenu
} from "src/components/views/documents/objectDocuments/DocumentsOfObjectsDataGridContextMenu.tsx";
import {faExternalLink, faFile, faFileArrowDown} from "@fortawesome/pro-regular-svg-icons";
import {DocumentDropZone} from "src/components/views/documents/import/DocumentDropZone.tsx";
import {AavoButton} from "src/components/common/buttons/AavoButton.tsx";
import {DocumentPreviewView} from "src/components/views/documents/preview/DocumentPreviewView.tsx";
import {AsyncButton} from "src/components/common/buttons/AsyncButton.tsx";
import {DocumentFileApi} from "src/api/generated/documents/api/documentFileApi.ts";
import {downloadFile} from "src/utils/fileDownloading.ts";
import {thumbnailColumn} from "src/components/views/documents/utils/thumbnails.tsx";
import {
    GridRowWithFileDropping,
    GridRowWithFileDroppingProps,
} from "src/components/common/dataGrid/dnd/GridRowWithFileDropping.tsx";
import {DropFileToDocumentView} from "src/components/views/documents/import/DropFileToDocumentView.tsx";
import {GenericDialogContext} from "src/components/common/dialogs/GenericDialogContext.ts";
import {useServerSideDataGridModel} from "src/components/common/dataGrid/gridModel/useServerSideDataGridModel";
import {ControlledAsyncFormCrudDataGrid} from "src/components/common/dataGrid/crud/ControlledAsyncFormCrudDataGrid";
import {DocumentConnectionView} from "src/api/generated/postgres/db/types/documents/tables/documentConnectionView.ts";
import {
    ConnectDocumentsToObjectView
} from "src/components/views/documents/connections/ConnectDocumentsToObjectView.tsx";
import {useContextOrThrow} from "src/utils/useContextOrThrow.tsx";
import {AavoObjectRef} from "src/api/generated/common/aavoObject/aavoObjectRef.ts";
import {useMediaQueryBreakpointDown} from "src/utils/breakpointUtils.ts";
import {ImportDocumentsButton} from "src/components/views/documents/import/ImportDocumentsButton.tsx";
import {LinkWithPlusIcon} from "src/components/common/icons/LinkWithPlusIcon";
import {getOnlyMember} from "src/utils/arrayUtils.ts";
import {GridProSlotsComponent} from "@mui/x-data-grid-pro";
import {RefreshableElementRef} from "src/utils/useRefreshRef.ts";
import {Typography} from "@mui/material";

import {DocumentsViewEditable} from "../types.ts";
import { Permissions } from "src/api/generated/iam/permissions.ts";
import { useHasPermission } from "src/components/views/erp/common/userPermissions.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";
import {useTenantCustomizations} from "src/tenantCustomizations/TenantCustomizationsContext.ts";
import {isDocumentCustomerSharingVisible} from "src/components/views/documents/documentCustomerSharing.ts";

export interface DocumentsOfObjectsDataGridProps {
    objectRefs: AavoObjectRef[];
    editable?: DocumentsViewEditable;
    selectedDocuments?: DocumentConnectionView[];
    onSelectionChanged?: (documents: DocumentConnectionView[]) => void;
    onDocumentFileChanged?: () => void;
    selectMainDocumentOnLoad?: boolean;
    gridId?: string;
    refreshRef?: RefreshableElementRef;
}

export const DocumentsOfObjectsDataGrid = ({
                                               objectRefs,
                                               editable = true,
                                               selectedDocuments,
                                               onSelectionChanged,
                                               onDocumentFileChanged,
                                               selectMainDocumentOnLoad = false,
                                               gridId = "60AEF12D15AD6E54",
                                               refreshRef,
                                           }: DocumentsOfObjectsDataGridProps) => {
    const hasPermission = useHasPermission();
    const {tenantConfig} = useTenantCustomizations();
    const {openDialog} = useContextOrThrow(GenericDialogContext);
    const customerSharingVisible = isDocumentCustomerSharingVisible(tenantConfig, objectRefs);
    const isMobile = useMediaQueryBreakpointDown("sm");

    const {dataGridProps, currentParams, refreshData, selectedRows, onlySelectedRow} = useServerSideDataGridModel({
        gridId: gridId,
        fetchData: (params) =>
            DocumentsOfObjectDataGridApi.getDocumentsOfObjects({
                objectRefs,
                ...params,
            }),
        getRowId: (row) => row.documentId,
        customFunctionUiActionView: "DOCUMENTS",
        initialParams: {
            categoryFilter: Array<number>(),
            searchQuery: "",
        },
        initialRowSelection:
            selectMainDocumentOnLoad ?
                (rows) => rows.find((document) => document.isObjectMainDocument)
            :   undefined,
        afterRefresh: () => {
            onDocumentFileChanged?.();
        },
        selectedRows: selectedDocuments,
        onSelectionChanged: onSelectionChanged,
        refreshRef: refreshRef,
    });

    const selectedDocumentIds = selectedRows.map((row) => row.documentId);

    const onlyObjectRef = getOnlyMember(objectRefs);

    return (
        <ControlledAsyncFormCrudDataGrid<DocumentConnectionView>
            disableMultipleRowSelection={false}
            columns={[
                integerColumn({
                    headerName: i18n.t("number_shortened"),
                    field: "documentId",
                    width: 60,
                    mobile: "cell",
                }),
                textColumn({
                    headerName: i18n.t("description_1"),
                    field: "description1",
                    width: 300,
                    mobile: "header",
                }),
                ...(customerSharingVisible ?
                    [
                        booleanColumn<DocumentConnectionView>({
                            headerName: i18n.t("shared_with_customer"),
                            field: "isSharedWithCustomer",
                            width: 160,
                        }),
                    ]
                :   []),
                textColumn({
                    headerName: i18n.t("type"),
                    field: "primaryFileExtension",
                    width: 80,
                    mobile: "cell",
                }),
                integerColumn({
                    headerName: i18n.t("revision"),
                    field: "revisionNumber",
                    width: 60,
                }),
                thumbnailColumn({
                    headerName: i18n.t("image"),
                    field: "thumbnail",
                }),
                textColumn({
                    headerName: i18n.t("description_2"),
                    field: "description2",
                    width: 250,
                    mobile: objectRefs.length > 1 ? "cell" : "details",
                }),
                textColumn({
                    headerName: i18n.t("category"),
                    field: "categoryName",
                    width: 200,
                    mobile: "cell",
                }),
                textColumn({
                    headerName: i18n.t("note"),
                    field: "note",
                    width: 200,
                }),
                textColumn({
                    headerName: i18n.t("created_by"),
                    field: "createdByUserName",
                }),
                dateTimeColumn({
                    headerName: i18n.t("created_at"),
                    field: "createdAt",
                    width: 150,
                }),
                booleanColumn({
                    headerName: i18n.t("pinned"),
                    field: "isPinned",
                }),
                booleanColumn({
                    headerName: i18n.t("object_main_document"),
                    field: "isObjectMainDocument",
                    width: 200,
                }),
            ]}
            listViewOnMobile={{
                header: (row) => `${row.documentId} | ${row.description1}`,
                detailsView: ({row}) => ({
                    type: "tabs",
                    items: [
                        {
                            title: i18n.t("preview"),
                            icon: faFile,
                            content: <DocumentPreviewView documentId={row.documentId}/>,
                        },
                    ],
                }),
            }}
            slots={{
                row: GridRowWithFileDropping as GridProSlotsComponent["row"],
            }}
            slotProps={{
                row: {
                    canDrop: editable === true,
                    onFilesDropped: (row: DocumentConnectionView, files) => onFilesDroppedToRow(row, files),
                } as GridRowWithFileDroppingProps,
            }}
            onRowDoubleClick={({row}) => {
                openDocumentPreview(row);
            }}
            actionBarComponents={
                <>
                    <AavoButton
                        icon={faExternalLink}
                        disabled={!onlySelectedRow}
                        tooltip={i18n.t("open")}
                        onClick={() => {
                            if (onlySelectedRow === undefined) return;
                            openDocumentPreview(onlySelectedRow);
                        }}
                    />
                    <AsyncButton
                        icon={faFileArrowDown}
                        tooltip={i18n.t("download")}
                        disabled={selectedDocumentIds.length === 0}
                        onClick={async () => {
                            const fileHandle = await DocumentFileApi.downloadDocumentsAsSingleFile({
                                documentIds: selectedDocumentIds,
                            });
                            downloadFile(fileHandle);
                        }}
                    />
                    <LazyMultiSelectField
                        label={i18n.t("category")}
                        value={currentParams.categoryFilter}
                        fetchOptions={() =>
                            DocumentsOfObjectDataGridApi.getDocumentCategoryOptionsForObjects({
                                objectRefs,
                            })
                        }
                        getOptionKey={(option) => option.documentCategoryId}
                        getOptionLabel={(option) => option.name}
                        onChange={async (values: number[]) => {
                            await refreshData({categoryFilter: values});
                        }}
                        sx={{
                            minWidth: 120,
                            display: {xs: "none", sm: "block"},
                        }}
                    />
                    <AavoTextField
                        label={i18n.t("search")}
                        onSubmit={async (value) => {
                            await refreshData({searchQuery: value});
                        }}
                    />
                    {editable === true && onlyObjectRef && hasPermission(Permissions.Documents.LINK_EXISTING) && (
                        <AsyncButton
                            iconEl={<LinkWithPlusIcon/>}
                            label={i18n.t("link")}
                            iconOnlyIfDown={"sm"}
                            variant={"outlined"}
                            onClick={async () => {
                                openDialog(({closeDialog}) => ({
                                    title: i18n.t("link_documents"),
                                    agentContext: {
                                        key: defineAgentUiContextKey("documents.objectDocuments.linkDocuments.dialog"),
                                        pathSegments: [i18n.t("link_documents")],
                                        data: {
                                            objectType: onlyObjectRef.objectType,
                                            objectId: onlyObjectRef.objectId ?? null,
                                        },
                                    },
                                    content: (
                                        <ConnectDocumentsToObjectView
                                            objectRef={onlyObjectRef}
                                            onDocumentsConnected={async () => {
                                                await closeDialog();
                                                await refreshData();
                                            }}
                                        />
                                    ),
                                }));
                            }}
                        />
                    )}
                    {editable === true &&
                        onlyObjectRef &&
                        (isMobile ?
                            <ImportDocumentsButton objectRef={onlyObjectRef} onSubmit={() => refreshData}/>
                            : <DocumentDropZone objectRef={onlyObjectRef} onSubmit={refreshData}/>)}
                </>
            }
            form={{
                dialogSize: "lg",
                dialogTitle: i18n.t("document_connection"),
                agentContext: (row) => ({
                    key: defineAgentUiContextKey("documents.objectDocument.formDialog"),
                    pathSegments:
                        row == null ?
                            [i18n.t("document_connection")]
                        :   [`${i18n.t("document")} ${row.documentId}`],
                    data: {
                        documentId: row?.documentId ?? null,
                        documentConnectionId: row?.documentConnectionId ?? null,
                        objectType: onlyObjectRef?.objectType ?? null,
                        objectId: onlyObjectRef?.objectId ?? null,
                    },
                }),
                component: ({row, onCompleted, onFormEdited}) => {
                    return (
                        <DocumentForm
                            objectRef={onlyObjectRef}
                            documentId={row?.documentId}
                            onCompleted={onCompleted}
                            onFormEdited={onFormEdited}
                        />
                    );
                },
                addRowEnabled: editable === true,
                editEnabled: editable === true,
            }}
            remove={{
                type: editable === true ? "enabled" : "hidden",
                action: async ({items}) => {
                    const deleteChoice = await chooseDocumentDeleteMethod(items.length);
                    if (deleteChoice === "interrupt") return "interrupt";

                    if (deleteChoice === "documents") {
                        await DocumentsOfObjectDataGridApi.deleteDocuments({
                            documentIds: items.map((item) => item.documentId),
                        });
                    } else {
                        await DocumentsOfObjectDataGridApi.deleteDocumentConnections({
                            documentConnections: items,
                        });
                    }
                },
                confirm: false,
            }}
            rowContextMenuComponents={(params) => (
                <DocumentsOfObjectsDataGridContextMenu
                    objectRefs={objectRefs}
                    refresh={refreshData}
                    editable={editable}
                    {...params}
                />
            )}
            agentContext={{
                key: defineAgentUiContextKey("documents.objectDocuments.grid"),
                data: {
                    objectRefs: objectRefs.map((objectRef) => ({
                        objectType: objectRef.objectType,
                        objectId: objectRef.objectId ?? null,
                    })),
                },
                rowFields: ["documentConnectionId", "documentId", "documentRevisionId", "categoryId", "sourceId"],
                selectedRowPathSegment: (row) => `${i18n.t("document")} ${row.documentId}`,
            }}
            {...dataGridProps}
        />
    );

    function onFilesDroppedToRow(row: DocumentConnectionView, files: File[]) {
        openDialog(({closeDialog}) => ({
            title: i18n.t("new_file"),
            size: "sm",
            agentContext: {
                key: defineAgentUiContextKey("documents.objectDocument.dropFile.dialog"),
                pathSegments: [`${i18n.t("document")} ${row.documentId}`, i18n.t("new_file")],
                data: {
                    documentId: row.documentId,
                    documentRevisionId: row.documentRevisionId,
                    documentConnectionId: row.documentConnectionId,
                },
            },
            content: (
                <DropFileToDocumentView
                    file={files[0]!}
                    documentId={row.documentId}
                    afterSubmit={async () => {
                        await closeDialog();
                        await refreshData();
                    }}
                />
            ),
        }));
    }

    async function chooseDocumentDeleteMethod(
        documentCount: number,
    ): Promise<"connections" | "documents" | "interrupt"> {
        return await new Promise((resolve) => {
            let result: "connections" | "documents" | "interrupt" = "interrupt";
            openDialog(({closeDialog}) => ({
                title: i18n.t("delete_selected"),
                size: "sm",
                content: (
                    <Typography sx={{marginX: 2, marginY: 1}}>
                        {i18n.t("choose_document_delete_method", {count: documentCount})}
                    </Typography>
                ),
                actions: (
                    <>
                        <AsyncButton
                            label={i18n.t("cancel")}
                            color="secondary"
                            onClick={async () => {
                                await closeDialog();
                            }}
                        />
                        <AsyncButton
                            label={i18n.t("remove_selected_document_connections", {count: documentCount})}
                            color="secondary"
                            onClick={async () => {
                                result = "connections";
                                await closeDialog();
                            }}
                        />
                        <AsyncButton
                            label={i18n.t("delete_selected_documents_completely", {count: documentCount})}
                            color="error"
                            onClick={async () => {
                                result = "documents";
                                await closeDialog();
                            }}
                        />
                    </>
                ),
                onClose: () => resolve(result),
                agentContext: {
                    key: defineAgentUiContextKey("documents.objectDocuments.delete.dialog"),
                    pathSegments: [i18n.t("delete_selected")],
                    data: {documentCount},
                },
            }));
        });
    }

    function openDocumentPreview(document: DocumentConnectionView) {
        openDialog(() => ({
            title: document.description1,
            agentContext: {
                key: defineAgentUiContextKey("documents.objectDocument.preview.dialog"),
                pathSegments: [`${i18n.t("document")} ${document.documentId}`],
                data: {
                    documentId: document.documentId,
                    documentRevisionId: document.documentRevisionId,
                    documentConnectionId: document.documentConnectionId,
                },
            },
            content: <DocumentPreviewView documentId={document.documentId}/>,
        }));
    }
};
